'use client';

import { useEffect, useMemo, useState } from 'react';

export interface RelativeTimeProps {
  date: string | Date;
  short?: boolean;
  verb?: string;
  className?: string;
}

type Unit = Intl.RelativeTimeFormatUnit;

function toValidDate(input: string | Date): Date | null {
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatRelative(target: Date, short: boolean): string {
  const now = Date.now();
  const diffMs = target.getTime() - now;
  const diffSeconds = Math.round(diffMs / 1000);
  const abs = Math.abs(diffSeconds);

  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto', style: short ? 'short' : 'long' });

  const table: Array<[number, Unit, number]> = [
    [60, 'second', 1],
    [60 * 60, 'minute', 60],
    [24 * 60 * 60, 'hour', 60 * 60],
    [7 * 24 * 60 * 60, 'day', 24 * 60 * 60],
    [30 * 24 * 60 * 60, 'week', 7 * 24 * 60 * 60],
    [365 * 24 * 60 * 60, 'month', 30 * 24 * 60 * 60],
  ];

  for (const [limit, unit, step] of table) {
    if (abs < limit) {
      const value = Math.round(diffSeconds / step);
      return rtf.format(value, unit);
    }
  }

  const value = Math.round(diffSeconds / (365 * 24 * 60 * 60));
  return rtf.format(value, 'year');
}

export function RelativeTime({ date, short = false, className = '', verb }: RelativeTimeProps) {
  const then = useMemo(() => toValidDate(date), [date]);

  const [relativeTime, setRelativeTime] = useState('');
  const [fullDateTime, setFullDateTime] = useState('');

  useEffect(() => {
    if (!then) {
      setRelativeTime('');
      setFullDateTime('');
      return;
    }

    const updateRelativeTime = () => setRelativeTime(formatRelative(then, short));

    setFullDateTime(
      then.toLocaleString(undefined, {
        dateStyle: 'long',
        timeStyle: 'short',
      }),
    );

    updateRelativeTime();

    const diffHrs = Math.abs(Date.now() - then.getTime()) / (1000 * 60 * 60);
    if (diffHrs < 24) {
      const interval = window.setInterval(updateRelativeTime, 60_000);
      return () => window.clearInterval(interval);
    }
  }, [then, short]);

  if (!then) return null;

  const descriptiveLabel = verb && fullDateTime ? `${verb} on ${fullDateTime}` : fullDateTime || undefined;

  return (
    <time dateTime={then.toISOString()} className={className} aria-label={descriptiveLabel} title={descriptiveLabel}>
      {relativeTime}
    </time>
  );
}

RelativeTime.displayName = 'RelativeTime';
